import { useEffect, useState } from "react";
import { api, todayHKT } from "../api";
import type { CigarStatus, Tag } from "../types";

const AREAS = [
  { k: "open_air", label: "無蓋露天" },
  { k: "covered_terrace", label: "有蓋" },
  { k: "rooftop", label: "天臺" },
  { k: "indoor", label: "室內" },
];

const VERDICTS: { k: CigarStatus; label: string }[] = [
  { k: "yes", label: "食得" },
  { k: "no", label: "唔得" },
  { k: "unsure", label: "唔確定" },
];

/**
 * 記錄一次到訪 —— 3 下搞完。呢個係成個 app 最常做嘅動作（每次去完餐廳），
 * 所以刻意冇任何多餘裝飾：兩個選擇 + remark + 儲存。
 *
 * 備註類別係**可選**嘅，而且排最後 —— 唔可以因為加咗標籤而令到「食得 +
 * 儲存」呢條最短路徑變長。標籤係為咗將來篩選，唔係每次都要填。
 */
export default function LogSheet({
  venueId,
  venueName,
  onClose,
  onSaved,
}: {
  venueId: number;
  venueName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [verdict, setVerdict] = useState<CigarStatus>("yes");
  const [area, setArea] = useState<string>("open_air");
  const [who, setWho] = useState<"me" | "friend">("me");
  const [remark, setRemark] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [tagList, setTagList] = useState<Tag[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // 標籤清單由後端嚟（單一真相來源）。攞唔到就唔顯示標籤區 ——
  // 冇得揀好過顯示一個假清單，令用戶以為自己揀咗啲唔存在嘅嘢。
  useEffect(() => {
    let live = true;
    api
      .tags()
      .then((t) => live && setTagList(t))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const toggle = (key: string) =>
    setPicked((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]));

  // 按 group 分開顯示。次序跟後端 tags.py，唔喺前端重排。
  const groups: { name: string; items: Tag[] }[] = [];
  for (const t of tagList) {
    const g = groups.find((x) => x.name === t.group);
    if (g) g.items.push(t);
    else groups.push({ name: t.group, items: [t] });
  }

  const save = async () => {
    setBusy(true);
    setErr("");
    try {
      const { id } = await api.addVisit(venueId, {
        cigar_confirmed: verdict,
        area_type: area,
        who,
        remark: remark.trim() || null,
        tags: picked,
        visited_on: todayHKT(),
      });
      if (photo) {
        try {
          await api.uploadPhoto(id, photo);
        } catch {
          setErr("到訪已記錄，但相上載失敗");
        }
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "儲存失敗");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sheet-scrim" onClick={onClose} role="dialog" aria-modal="true">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__title">今日去咗{venueName}</div>
        <div className="sheet__sub">{todayHKT()}　·　{who === "me" ? "你" : "朋友"}</div>

        <div className="sec">食唔食得雪茄</div>
        <div className="opts">
          {VERDICTS.map((v) => (
            <button
              key={v.k}
              className={`opts__item ${verdict === v.k ? "opts__item--on" : ""}`}
              onClick={() => setVerdict(v.k)}
              aria-pressed={verdict === v.k}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="sec">坐喺邊</div>
        <div className="opts">
          {AREAS.map((a) => (
            <button
              key={a.k}
              className={`opts__item ${area === a.k ? "opts__item--on" : ""}`}
              onClick={() => setArea(a.k)}
              aria-pressed={area === a.k}
            >
              {a.label}
            </button>
          ))}
        </div>

        <div className="sec">邊個去</div>
        <div className="opts">
          {(["me", "friend"] as const).map((w) => (
            <button
              key={w}
              className={`opts__item ${who === w ? "opts__item--on" : ""}`}
              onClick={() => setWho(w)}
              aria-pressed={who === w}
            >
              {w === "me" ? "你" : "朋友"}
            </button>
          ))}
        </div>

        {groups.length > 0 && (
          <>
            <div className="sec">
              備註類別
              <span className="sec__hint">
                {picked.length ? `已揀 ${picked.length} 個` : "可唔揀"}
              </span>
            </div>
            {groups.map((g) => (
              <div className="chipgroup" key={g.name}>
                <div className="chipgroup__name">{g.name}</div>
                <div className="chips">
                  {g.items.map((t) => (
                    <button
                      key={t.key}
                      className={`chip ${picked.includes(t.key) ? "chip--on" : ""}`}
                      onClick={() => toggle(t.key)}
                      aria-pressed={picked.includes(t.key)}
                      title={t.venue_count ? `${t.venue_count} 間` : "仲未用過"}
                    >
                      {t.label_zh}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}

        <div className="field" style={{ marginTop: 13 }}>
          <label className="field__label" htmlFor="photo">
            加相（會自動剝走 GPS）
          </label>
          <input
            id="photo"
            className="input"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="remark">寫低啲嘢</label>
          <textarea
            id="remark"
            className="textarea"
            placeholder="例如伙記冇理、要問經理"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </div>

        {err && <div className="err">{err}</div>}

        <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
          <button className="btn btn--plain" onClick={onClose} style={{ flex: 1 }}>
            取消
          </button>
          <button className="btn" onClick={save} disabled={busy} style={{ flex: 2 }}>
            {busy ? "儲存緊…" : "記錄今日到訪"}
          </button>
        </div>
      </div>
    </div>
  );
}
